import React, { Key, useState, useEffect } from 'react';
import type { ColumnsType } from 'antd/es/table';
import { FunnelSimple, Plus, X } from '@phosphor-icons/react';
import {
  ColumnSelector,
  TableCommon,
  SkeletonTable,
  // ExcelOperationsHandler,
  TopHeader,
  ButtonCommon,
  showAlertNotification,
} from 'components/common';
import { Columns, QueryParams } from 'types';
import { useNavigate } from 'react-router-dom';
import { formatPrice } from 'utils';
import { buildFullParams, parseSimpleFilter } from 'utils/filterBuilder';
import { setTableParams, TableCacheItem } from 'stores/slices/tableCacheSlice';
import { EInvoice, InvoiceRecord } from 'models/EInvoice';
import { format } from 'date-fns';
import { useEInvoiceOperation } from 'hooks/useEInvoiceOperation';
import FilterEInvoice from 'components/common/Filter/FilterEInvoice';

const InvoiceListPage: React.FC = () => {
  const params = new URLSearchParams(window.location.search);
  const isPreviewMode = params.get('preview') === 'true';
  // const [isPerformingOperation, setIsPerformingOperation] = useState<boolean>(false);
  const [eInvoice, setEInvoice] = useState<InvoiceRecord[]>([]);
  const [isLoadingDownload, setIsLoadingDownload] = useState<boolean>(false);
  const [idFileDownload, setIdFileDownload] = useState<string>();
  const {
    handleDownload,
    eInvoiceData,
    isLoading,
    // isFetching,
    filteredQuery,
    setFilteredQuery,
    dispatch,
    pageKey,
    tableState,
    handleApplyFilter,
    handleClearFilter,
    numberFilter,
  } = useEInvoiceOperation();
  const [isFilterPanelVisible, setIsFilterPanelVisible] = useState(false);
  const navigate = useNavigate();
  useEffect(() => {
    if (eInvoiceData?.data?.rows) {
      setEInvoice(eInvoiceData.data.rows);
      dispatch(
        setTableParams({
          pageKey,
          params: {
            lastFetched: Date.now(),
            search: filteredQuery.searchValue ?? '',
            page: filteredQuery.page ?? 1,
            pageSize: filteredQuery.size ?? 10,
            filter: filteredQuery.filter ?? '',
          } as unknown as Partial<TableCacheItem<unknown>>,
        }),
      );
    }
  }, [eInvoiceData]);
  // useEffect(() => {
  //   setIsPerformingOperation(isFetching);
  // }, [isFetching]);

  const eInvoiceColumns: ColumnsType<InvoiceRecord> = [
    {
      title: 'MẪU SỐ - KÝ HIỆU',
      dataIndex: 'invoiceSeries',
      key: 'invoiceSeries',
      sorter: (a, b) => (a?.invoiceSeries || '')?.localeCompare((b?.invoiceSeries || '') as string),
      filterSearch: false,
    },
    {
      title: 'SỐ HOÁ ĐƠN',
      dataIndex: 'invoiceNo',
      key: 'invoiceNo',
      sorter: (a, b) => (a?.invoiceNo || '')?.localeCompare((b?.invoiceNo || '') as string),
      filterSearch: false,
    },
    {
      title: 'TRẠNG THÁI',
      dataIndex: 'invoiceTypeText',
      key: 'invoiceTypeText',
      sorter: (a, b) => (a?.invoiceTypeText || '')?.localeCompare((b?.invoiceTypeText || '') as string),
      filterSearch: false,
    },
    {
      title: 'NGÀY LẬP',
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      render: (date) => <div>{format(new Date(date), 'HH:mm, dd/MM/yyyy')}</div>,
      filterSearch: false,
    },
    {
      title: 'MÃ THAM CHIẾU',
      dataIndex: 'referenceCode',
      key: 'referenceCode',
      sorter: (a, b) => (a?.referenceCode || '')?.localeCompare((b?.referenceCode || '') as string),
      filterSearch: false,
    },
    {
      title: 'TÊN KHÁCH HÀNG',
      dataIndex: 'buyerName',
      key: 'buyerName',
      sorter: (a, b) => (a?.buyerName || '')?.localeCompare((b?.buyerName || '') as string),
      filterSearch: false,
    },
    {
      title: 'MÃ SỐ THUẾ',
      dataIndex: 'buyerTaxCode',
      key: 'buyerTaxCode',
      sorter: (a, b) => (a?.buyerTaxCode || '')?.localeCompare((b?.buyerTaxCode || '') as string),
      filterSearch: false,
    },
    {
      title: 'GIÁ TRỊ',
      dataIndex: 'totalAfterTax',
      key: 'totalAfterTax',
      sorter: (a, b) => (a?.totalAfterTax || '')?.localeCompare((b?.totalAfterTax || '') as string),
      render: (text) => <span className=''>{formatPrice(text)} VNĐ</span>,
      filterSearch: false,
    },
  ];

  const eInvoiceColumnsCheck: Columns[] = eInvoiceColumns.map((column) => ({
    key: column.key as string,
    title: column.title as string,
  }));

  const [checkedList, setCheckedList] = useState<(Key | undefined)[]>(eInvoiceColumns.map((column) => column.key));

  const getFilteredColumns = () => {
    return eInvoiceColumns.map((item) => ({
      ...item,
      hidden: !checkedList.includes(item.key),
    }));
  };

  const handlePagination = (query: QueryParams) => {
    const columnArray = ['status.code'];
    const filterQuery = buildFullParams(query, undefined, columnArray);
    setFilteredQuery((prev) => ({
      ...prev,
      ...filterQuery,
    }));
  };

  function handleView(record: EInvoice): void {
    navigate(`/invoices/electronic-invoices/view/${record.id}`);
  }
  async function handleDownloadFile(record: EInvoice): Promise<void> {
    try {
      if (isLoadingDownload || idFileDownload) {
        showAlertNotification({
          type: 'warning',
          title: 'Tạo hoá đơn điện tử',
          message: `Đang quá trình tải hoá đơn điện tử khác, vui lòng đợi trong giây lát.`,
        });
        return;
      }
      setIsLoadingDownload(true);
      setIdFileDownload(record.id);
      await handleDownload(record.id);
    } catch (error) {
      console.log('handleDownloadFile: ', error);
    } finally {
      setIsLoadingDownload(false);
      setIdFileDownload('');
    }
  }
  // function handleCopy(record: EInvoice): void {
  //   navigate(`invoices/create-invoice?copyId=${record.id}`);
  // }
  const handleCreateInvoice = (): void => {
    try {
      navigate('/invoices/electronic-invoices/create-invoice');
    } catch (error) {
      console.log('handleCreateProduct: ', error);
    }
  };
  const toggleFilterPanel = () => {
    setIsFilterPanelVisible((prev) => !prev);
  };
  return (
    <div className='flex flex-col h-full relative'>
      {!isPreviewMode && (
        <TopHeader
          className='mb-4 rounded-lg'
          title='DANH SÁCH HOÁ ĐƠN ĐIỆN TỬ'
          subTitle='Tất cả các hoá đơn điện tử được hiển thị tại đây'
        />
      )}
      <div className='w-full bg-[var(--textWhite)]'>
        <div className='flex lg:flex-row flex-col gap-2 justify-between  items-center px-[24px] py-[12px]'>
          <h1 className='text-[18px] leading-[26px] font-[700]'>DANH SÁCH HOÁ ĐƠN ĐIỆN TỬ</h1>
          <div className='flex gap-2'>
            <ButtonCommon
              icon={<FunnelSimple size={20} />}
              color='primary'
              size='xs'
              radius='rounded'
              onClick={toggleFilterPanel}
            >
              Bộ lọc ({numberFilter || 0})
            </ButtonCommon>
            <ColumnSelector
              allColumns={eInvoiceColumnsCheck}
              selectedColumns={checkedList}
              onChange={(selected) => setCheckedList(selected)}
            />
            {/* <ExcelOperationsHandler
              entityType='sale-order'
              entityName='hoá đơn điện tử'
              disabled={isPerformingOperation}
            /> */}
            <ButtonCommon
              icon={<Plus size={20} />}
              color='primary'
              onClick={handleCreateInvoice}
              size='xs'
              radius='rounded'
            >
              Thêm hoá đơn mới
            </ButtonCommon>
          </div>
        </div>
      </div>
      <div className={`fixed inset-0 z-50 flex justify-start ${isFilterPanelVisible ? 'visible' : 'invisible'}`}>
        <div
          className={`absolute inset-0 bg-black transition-opacity duration-300 ${
            isFilterPanelVisible ? 'opacity-50' : 'opacity-0'
          }`}
          onClick={() => setIsFilterPanelVisible(false)}
        ></div>

        <div
          className={`relative bg-white min-w-[400px] flex flex-col max-w-[450px] h-full transform transition-transform duration-300 ease-out shadow-xl overflow-auto ${
            isFilterPanelVisible ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <div className='sticky top-0 bg-white p-4 border-b border-gray-200 flex items-center justify-between z-10'>
            <div className='text-lg font-bold'>Bộ lọc</div>
            <button
              className='text-gray-500 hover:text-gray-700 focus:outline-none'
              onClick={() => setIsFilterPanelVisible(false)}
            >
              <X size={24} />
            </button>
          </div>
          <div className='p-4 flex-1'>
            <FilterEInvoice onApplyFilter={handleApplyFilter} onClearFilter={handleClearFilter} />
          </div>
        </div>
      </div>
      <div className='flex-grow bg-[var(--textWhite)] flex flex-col'>
        <div className='flex-grow flex flex-col'>
          {isLoading ? (
            <SkeletonTable columns={eInvoiceColumns.length} rows={10} hasHeader={true} />
          ) : (
            <TableCommon<InvoiceRecord>
              columns={getFilteredColumns()}
              dataSource={eInvoice || []}
              defaultPageSize={Number(filteredQuery.size) || 10}
              count={eInvoiceData?.data?.total || 0}
              displayView={true}
              onView={handleView}
              onEdit={() => {}}
              onDownload={handleDownloadFile}
              tableProps={{
                scroll: { x: 'max-content' },
                size: 'large',
                className: 'h-full',
                style: {
                  minHeight: !isPreviewMode ? 'calc(100vh - 365px)' : '100vh',
                  display: 'flex',
                  flexDirection: 'column',
                },
                loading: false,
              }}
              onPaginationChange={handlePagination}
              currentPage={Number(filteredQuery.page)}
              searchColumnDefault={parseSimpleFilter(tableState?.filter)}
              downLoadLoading={isLoadingDownload}
              idFileDownload={idFileDownload}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default InvoiceListPage;
